import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Creators as ReportActions } from "../../../store/ducks/report";
// Components
import { Col, DatePicker, Input, Row, Select, Spin, notification } from "antd";
import CustomDivider from "../../utils/customDivider";
import {
  Container,
  ExtensionButton,
  ExtensionContainer,
  Title,
} from "./styles";
// Services
import { Form, Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { getCattleProducersDropdown } from "../../../services/cattleProducerService";
import { processReportContracts } from "../../../services/reportService";
import ExportPdfIcon from "../../utils/icons/export/pdf";
import fileReader from "../../../utils/fileReader";
import { momentTimeZoneHour } from "../../../utils/dateUtils";

const INITIAL_FORM_STATE = {
  cattleProducerId: null,
  contractDate: null,
  identificationContract: "",
};

const validationSchema = Yup.object().shape({
  cattleProducerId: Yup.string().nullable().required(),
});

const ExportReportContractsModal = () => {
  const formikRef = useRef();
  const [cattleProducerOptions, setCattleProducerOptions] = useState([]);
  const [isLoadingCattleProducers, setIsLoadingCattleProducers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const { isModalContractsReportVisible: modalVisible } = useSelector(
    (state) => state.report
  );

  const dispatch = useDispatch();

  const fetchCattleProducers = useCallback(async () => {
    setIsLoadingCattleProducers(true);
    try {
      const response = await getCattleProducersDropdown({ groupId, farmId });
      const data = response?.data;
      const all = data?.results ?? (Array.isArray(data) ? data : []);
      setCattleProducerOptions(all);
    } catch (error) {
    } finally {
      setIsLoadingCattleProducers(false);
    }
  }, [groupId, farmId]);

  async function handleSubmit(values) {
    setIsLoading(true);

    notification.info({
      message:
        "O seu relatório está sendo preparado e logo será feito o download!",
    });

    const producer = cattleProducerOptions.find(
      (p) => p.id === values.cattleProducerId
    );
    const producerName = producer
      ? producer.name
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/\s+/g, "_")
      : "Produtor";
    const requestDate = momentTimeZoneHour(new Date()).format("YYYYMMDDHHmmss");

    const body = {
      cattleProducerId: values.cattleProducerId,
      contractDate: values.contractDate
        ? moment(values.contractDate).format("YYYY-MM-DD")
        : null,
      identificationContract: values.identificationContract || null,
    };

    setTimeout(() => {
      setIsLoading(false);
      dispatch(ReportActions.hideModal());
    }, 100);

    try {
      const { data } = await processReportContracts({ groupId, farmId, body });

      const downloadUrl = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute(
        "download",
        `Contratos_${producerName}_${requestDate}.zip`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      notification.success({
        message: translation.successGenerateReport,
      });
    } catch (error) {
      let message = translation.errorGenerateReport;
      if (error?.response) {
        try {
          const { data } = error.response;
          const file = await fileReader(data);
          if (typeof file === "string") {
            const result = JSON.parse(file);
            if (result.code === 5035) {
              message =
                translation.contractsReportModal.messages.errorNoDataFound;
            }
          }
        } catch (readError) {}
      }
      const description =
        message === translation.errorGenerateReport ? (
          <ul style={{ paddingLeft: 16, marginBottom: 0 }}>
            <li>
              {translation.contractsReportModal.messages.checkVigente}
            </li>
            <li>
              {translation.contractsReportModal.messages.checkActiveLot}
            </li>
          </ul>
        ) : undefined;
      notification.error({ message, description });
    }
  }

  useEffect(() => {
    if (formikRef && formikRef.current && modalVisible) {
      formikRef.current.resetForm(INITIAL_FORM_STATE);
    }
    return () => {};
  }, [modalVisible]);

  useEffect(() => {
    if (modalVisible) {
      fetchCattleProducers();
    }
    return () => {};
  }, [fetchCattleProducers, modalVisible]);

  return (
    <Container
      width={500}
      visible={modalVisible}
      centered
      closable={true}
      footer={null}
      onCancel={() => dispatch(ReportActions.hideModal())}
    >
      <Spin spinning={isLoading}>
        <Row type="flex">
          <Col span={24}>
            <Title>{translation.contractsReportModal.title}</Title>
          </Col>
        </Row>
        <Formik
          ref={formikRef}
          initialValues={INITIAL_FORM_STATE}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values, errors, submitCount }) => (
            <Form>
              <Row gutter={16}>
                <Col span={24}>
                  <label
                    className={
                      errors.cattleProducerId && submitCount > 0 ? "error" : ""
                    }
                  >
                    {translation.contractsReportModal.form.cattleProducerId} *
                  </label>
                  <Select
                    loading={isLoadingCattleProducers}
                    value={values.cattleProducerId || undefined}
                    onChange={(val) => setFieldValue("cattleProducerId", val)}
                    style={{ width: "100%" }}
                    placeholder={translation.defaultSelectPlaceholder}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {cattleProducerOptions.map((p) => (
                      <Select.Option key={p.id} value={p.id}>
                        {p.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <label>
                    {translation.contractsReportModal.form.contractDate}
                  </label>
                  <DatePicker
                    value={values.contractDate || null}
                    format="DD/MM/YYYY"
                    placeholder={translation.defaultDatePickerPlaceholder}
                    style={{ width: "100%" }}
                    onChange={(date) => setFieldValue("contractDate", date)}
                  />
                </Col>
                <Col span={12}>
                  <label>
                    {
                      translation.contractsReportModal.form
                        .identificationContract
                    }
                  </label>
                  <Input
                    value={values.identificationContract}
                    placeholder={
                      translation.contractsReportModal.form
                        .identificationContractPlaceholder
                    }
                    onChange={(e) =>
                      setFieldValue("identificationContract", e.target.value)
                    }
                  />
                </Col>
              </Row>
              <CustomDivider dashed title="EXTENSÃO" />
              <Row type="flex">
                <Col span={24}>
                  <ExtensionContainer>
                    <ExtensionButton type="submit">
                      <ExportPdfIcon />
                    </ExtensionButton>
                  </ExtensionContainer>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </Spin>
    </Container>
  );
};

export default ExportReportContractsModal;
